import { useEffect, useId, useRef, type ReactNode } from 'react'

import { IconButton } from './Button.js'
import { Chip } from './Chip.js'
import styles from './Sheet.module.css'

interface SheetProps {
  readonly open: boolean
  readonly title: string
  /**
   * The corner control's accessible name, or `null` for no corner control.
   *
   * Defaults to "Done", because the sheets the redesign introduces commit as
   * you go and have nothing to cancel — picking a filter applies it, picking a
   * time sets it.
   *
   * A sheet that asks a question passes `null` instead. It already offers a
   * Cancel button in its actions, and a corner control that does the same thing
   * either duplicates that name — two "Cancel" buttons in one dialog, which is
   * exactly as confusing to hear as it sounds — or invents a second word for
   * one action. Both of this app's existing sheets are confirmations.
   */
  readonly dismissLabel?: string | null
  readonly onClose: () => void
  readonly children: ReactNode
}

/** Marks the history entry a sheet pushes, so it can discard its own. */
const SHEET_HISTORY_KEY = 'anytrackerSheet'

function isOnSheetHistoryEntry(): boolean {
  const state: unknown = window.history.state
  return typeof state === 'object' && state !== null && SHEET_HISTORY_KEY in state
}

/**
 * A modal sheet, on the platform `<dialog>` element.
 *
 * `showModal()` gives focus trapping, Escape-to-close, inertness of the page
 * behind, and top-layer stacking that no `z-index` can lose. Re-implementing
 * those is a well-known way to ship a dialog a screen reader can wander out of.
 *
 * The dialog fills the viewport and is transparent; what you see is a scrim and
 * a panel inside it. That is what lets the scrim be a **real button** rather
 * than a `::backdrop` pseudo-element, which cannot be clicked, named, or
 * reached by anyone not using a mouse.
 */
export function Sheet({
  open,
  title,
  dismissLabel = 'Done',
  onClose,
  children,
}: SheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  /*
   * Held in a ref so the effects below can depend on `open` alone.
   *
   * Every caller passes an inline arrow, so `onClose` is a new function on
   * every render. An effect that listed it as a dependency would re-run on
   * every render — and the history effect below would then push a new entry
   * each time, burying the screen under entries that all look identical.
   */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  /* Opening and closing, and where the focus goes at each end of that. */
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !open) {
      return
    }

    const trigger =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    // Calling showModal() on an already-open dialog throws.
    if (!dialog.open) {
      dialog.showModal()
    }

    /*
     * The panel, not whatever happens to be first.
     *
     * `showModal()` focuses the first focusable descendant, which here is the
     * scrim — so the sheet would open by announcing "Close, button", which is
     * the one thing in it the user did not come for. Landing on the panel reads
     * the title instead, and Tab from there reaches the content in order.
     */
    panelRef.current?.focus()

    return () => {
      if (dialog.open) {
        dialog.close()
      }
      /*
       * `<dialog>` returns focus to the trigger itself when it closes, but only
       * while the element is still in the document. Every caller here unmounts
       * the sheet as it closes, which detaches it first and drops focus to
       * `<body>` — leaving a keyboard user at the top of the page with no idea
       * where they were. So the return is done here rather than trusted.
       */
      if (trigger?.isConnected) {
        trigger.focus()
      }
    }
  }, [open])

  /*
   * The back button closes the sheet rather than leaving the screen.
   *
   * On a phone this is the difference between an app and a bug: the hardware
   * back gesture is how people dismiss things, and a sheet that ignores it
   * appears to throw the user off the screen they were working on.
   *
   * The pushed entry copies the current history state rather than replacing it.
   * React Router keeps its own bookkeeping there and reads it back on popstate
   * to work out how far it moved; copying it means the entry looks like the
   * same position, so the router treats the pop as a no-op instead of a
   * navigation. The URL never changes — a sheet is not a location.
   */
  useEffect(() => {
    if (!open) {
      return
    }

    const current: unknown = window.history.state
    const base = typeof current === 'object' && current !== null ? current : {}
    window.history.pushState({ ...base, [SHEET_HISTORY_KEY]: true }, '')

    let poppedByBrowser = false
    const onPopState = () => {
      poppedByBrowser = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
      /*
       * Closing any other way — Done, the scrim, Escape — leaves the entry on
       * the stack, and the next back press would be spent silently undoing it.
       * The guard is for the case where the sheet's own content navigated: the
       * entry is no longer ours to discard, and going back would undo the
       * navigation instead.
       */
      if (!poppedByBrowser && isOnSheetHistoryEntry()) {
        window.history.back()
      }
    }
  }, [open])

  /*
   * The page behind does not scroll.
   *
   * A modal `<dialog>` blocks interaction with the document, but a drag that
   * starts on the scrim still scrolls the page underneath in some browsers —
   * and the scrim is exactly where a thumb lands when reaching past a sheet.
   * The previous value is restored rather than cleared, so this cannot quietly
   * become the owner of a property it did not set.
   */
  useEffect(() => {
    if (!open) {
      return
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  return (
    <dialog
      // Positioned by the platform, not the page: exempt from the gutter.
      data-bleed
      ref={dialogRef}
      className={styles['sheet']}
      aria-labelledby={titleId}
      /*
       * Escape closes through the platform's own `cancel` event, so the
       * parent's state cannot drift out of sync with what is on screen.
       *
       * There is deliberately no `onClose` handler on the element: the only
       * thing that calls `close()` is this component's own teardown, which has
       * already been told. Wiring it up would turn every close into a second
       * `onClose` call on a parent that had just finished closing.
       */
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
    >
      <button
        type="button"
        className={`${styles['scrim']} fade`}
        aria-label="Close"
        onClick={onClose}
      />
      {/*
        `sheet-in` is the global class from `motion.css` rather than an
        animation declared here, because that is where the reduced-motion
        override lives. A local `animation: sheet-in ...` would look identical
        and go on sliding for the people who asked it not to.
      */}
      <div className={`${styles['panel']} sheet-in`} ref={panelRef} tabIndex={-1}>
        <div className={styles['header']}>
          <h2 className={styles['title']} id={titleId}>
            {title}
          </h2>
          {dismissLabel !== null && (
            <IconButton label={dismissLabel} onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
          )}
        </div>
        <div className={styles['body']}>{children}</div>
      </div>
    </dialog>
  )
}

export interface SheetOption {
  readonly id: string
  readonly label: string
  /** A dot in this colour, for tracks and tags that carry one. */
  readonly colour?: string
  readonly selected: boolean
  readonly onChoose: () => void
}

interface SheetOptionGroupProps {
  readonly label: string
  readonly options: readonly SheetOption[]
}

/**
 * A labelled row of pill options, wrapping.
 *
 * The idiom the redesign uses for both of its sheets — filtering history and
 * changing the time — and the reason `Sheet` takes arbitrary children as well:
 * the two sheets only _look_ alike, and the next one will not be a list of
 * options at all.
 *
 * Options are `Chip`s, which already meet the 44px touch target and already
 * draw the colour dot. A second pill implementation here would be one more
 * place for the selected state to drift.
 */
export function SheetOptionGroup({ label, options }: SheetOptionGroupProps) {
  const labelId = useId()

  return (
    <div className={styles['group']} role="group" aria-labelledby={labelId}>
      <div className={styles['groupLabel']} id={labelId}>
        {label}
      </div>
      <div className={styles['options']}>
        {options.map((option) => (
          <Chip
            key={option.id}
            {...(option.colour === undefined ? {} : { colour: option.colour })}
            selected={option.selected}
            onClick={option.onChoose}
          >
            {option.label}
          </Chip>
        ))}
      </div>
    </div>
  )
}
